import { Box, cssVar, Flex, Text } from '@cad-arc';

/**
 * Chrome for the camera video player, built to the "two modes, one control bar" design.
 *
 * Native `<video controls>` cannot carry this: browsers expose no supported styling surface for
 * their control bar (Chrome's `::-webkit-media-controls-*` pseudo-elements are unspecified and
 * unreliable). So the video renders bare and everything visible here is ours.
 *
 * Colours come from the raw ramps rather than the semantic ones. `negative.default` is tuned for
 * text and borders on CAD's surfaces and reads pink at badge size against video; `raw.red.600` is
 * the flat red the design shows.
 */

/** #c22e31 — the flat red of the design's LIVE badge and live scrubber. */
const RED = cssVar('arc.colors.raw.red.600');
/** #FFFFFF, for text on the filled red badge. Fixed rather than themed: the fill is always red. */
const ON_RED = cssVar('arc.colors.raw.white');
/**
 * Backing for the chips that sit over video. Opaque black rather than translucent: at 55% the
 * footage showed through and the chip read as smeared rather than as a label.
 */
const OVER_VIDEO_BACKING = cssVar('arc.colors.raw.black');
/** The design's amber, for the reviewing state and the event ticks. */
const AMBER = cssVar('arc.colors.raw.orange.500');

export const VIDEO_MODES = ['live', 'reviewing', 'signalLost', 'eventClip'] as const;
export type VideoMode = (typeof VIDEO_MODES)[number];

const MODE_LABELS: Record<VideoMode, string> = {
    live: 'LIVE',
    reviewing: 'REVIEWING',
    signalLost: 'SIGNAL LOST',
    eventClip: 'EVENT CLIP',
};

/**
 * Not Arc's `Status`, which carries one colour per state and so cannot tell LIVE from SIGNAL LOST —
 * both are red. The design separates them by fill: LIVE filled red, SIGNAL LOST a red outline,
 * REVIEWING an amber outline, EVENT CLIP a neutral outline.
 */
function getModeColors(mode: VideoMode): { border: string; background?: string; text: string } {
    switch (mode) {
        case 'live':
            return { border: RED, background: RED, text: ON_RED };
        case 'reviewing':
            return { border: AMBER, text: AMBER };
        case 'signalLost':
            return { border: RED, text: RED };
        case 'eventClip':
        default:
            return {
                border: cssVar('arc.colors.border.default'),
                text: cssVar('arc.colors.text.secondary'),
            };
    }
}

export function VideoModeBadge({
    mode,
    suffix,
}: {
    mode: VideoMode;
    /** e.g. `4:12 BEHIND` on the reviewing chip. */
    suffix?: string;
}): React.ReactElement {
    const colors = getModeColors(mode);

    return (
        <Flex
            height="22px"
            alignItems="center"
            gap={1}
            paddingX={2}
            borderRadius="sm"
            flexShrink={0}
            style={{
                border: `1px solid ${colors.border}`,
                background: colors.background ?? OVER_VIDEO_BACKING,
            }}
        >
            <Box
                width="6px"
                height="6px"
                borderRadius="full"
                flexShrink={0}
                style={{ background: colors.text }}
            />
            <Text
                variant="caption"
                fontFamily="mono"
                fontWeight="bold"
                style={{ color: colors.text }}
            >
                {[MODE_LABELS[mode], suffix].filter(Boolean).join(' · ')}
            </Text>
        </Flex>
    );
}

/** Mono readout chip, backed in opaque black because it sits over footage. */
export function VideoReadout({ children }: { children: React.ReactNode }): React.ReactElement {
    return (
        <Flex
            height="22px"
            alignItems="center"
            paddingX={1}
            borderRadius="sm"
            flexShrink={0}
            style={{ background: OVER_VIDEO_BACKING }}
        >
            <Text variant="caption" fontFamily="mono" style={{ color: ON_RED }}>
                {children}
            </Text>
        </Flex>
    );
}

/** The one accent in backtracked mode, because it is the recovery path back to live. */
export function ReturnToLiveButton({ onClick }: { onClick: () => void }): React.ReactElement {
    return (
        <Flex
            as="button"
            type="button"
            onClick={onClick}
            height="22px"
            alignItems="center"
            gap={1}
            paddingX={2}
            borderRadius="sm"
            cursor="pointer"
            flexShrink={0}
            style={{ background: RED }}
        >
            <Text
                variant="caption"
                fontFamily="mono"
                fontWeight="bold"
                style={{ color: ON_RED }}
            >
                ▶ RETURN TO LIVE
            </Text>
        </Flex>
    );
}

export type ScrubberTick = {
    /** 0–1 position along the bar. */
    position: number;
    label: string;
    /** Seconds to seek to — two seconds before the event, per the design. */
    seekToSeconds: number;
};

/**
 * Live: filled red with the handle parked at the right edge, which is how "there is nothing ahead
 * of you" reads without a label. Backtracked: blue played, grey remainder, handle where you are.
 *
 * Event ticks persist across both modes and are placed by wall clock against the seekable window,
 * so a tick only appears for an alert that really falls inside what the stream lets you seek to.
 */
export function VideoScrubber({
    playedFraction,
    ticks,
    mode,
    onSeekToFraction,
    onSeekToSeconds,
}: {
    playedFraction: number;
    ticks: ScrubberTick[];
    mode: VideoMode;
    onSeekToFraction: (fraction: number) => void;
    onSeekToSeconds: (seconds: number) => void;
}): React.ReactElement {
    const clamped = Math.min(Math.max(playedFraction, 0), 1);
    const playedColor = mode === 'live' ? RED : cssVar('arc.colors.brand.default');

    return (
        <Box
            position="relative"
            height="20px"
            flexGrow={1}
            minWidth={0}
            cursor="pointer"
            onClick={(event) => {
                const bounds = event.currentTarget.getBoundingClientRect();
                if (bounds.width > 0) {
                    onSeekToFraction((event.clientX - bounds.left) / bounds.width);
                }
            }}
        >
            {/* 6px rather than a hairline so the full rounding actually reads. */}
            <Box
                position="absolute"
                top="7px"
                left={0}
                right={0}
                height="6px"
                borderRadius="full"
                backgroundColor="border.default"
            />
            <Box
                position="absolute"
                top="7px"
                left={0}
                height="6px"
                borderRadius="full"
                style={{ width: `${clamped * 100}%`, background: playedColor }}
            />
            {ticks.map((tick) => (
                <Box
                    key={`${tick.position}-${tick.label}`}
                    position="absolute"
                    top="4px"
                    width="2px"
                    height="12px"
                    title={tick.label}
                    cursor="pointer"
                    onClick={(event) => {
                        // Click seeks to the event rather than to where the pointer landed.
                        event.stopPropagation();
                        onSeekToSeconds(tick.seekToSeconds);
                    }}
                    style={{ left: `${Math.min(Math.max(tick.position, 0), 1) * 100}%`, background: AMBER }}
                />
            ))}
            <Box
                position="absolute"
                top="2px"
                width="16px"
                height="16px"
                borderRadius="full"
                pointerEvents="none"
                style={{
                    left: `calc(${clamped * 100}% - 8px)`,
                    background: cssVar('arc.colors.text.primary'),
                    border: `2px solid ${playedColor}`,
                }}
            />
        </Box>
    );
}

/**
 * Full screen, as the design specifies it: a 16px corner-bracket glyph inside a 44px hit area.
 * Drawn inline because Arc's icon set has no corner-bracket glyph — `ExpandWindow` is an
 * arrows-out shape.
 */
export function FullScreenButton({ onClick }: { onClick: () => void }): React.ReactElement {
    return (
        <Flex
            as="button"
            type="button"
            aria-label="Full screen"
            onClick={onClick}
            width="44px"
            height="24px"
            alignItems="center"
            justifyContent="center"
            backgroundColor="transparent"
            cursor="pointer"
            flexShrink={0}
            style={{ color: cssVar('arc.colors.text.primary') }}
        >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                <path
                    d="M1 5.5V1h4.5M10.5 1H15v4.5M15 10.5V15h-4.5M5.5 15H1v-4.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                />
            </svg>
        </Flex>
    );
}

export function TransportButton({
    label,
    onClick,
    isDisabled = false,
    children,
}: {
    label: string;
    onClick?: () => void;
    isDisabled?: boolean;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <Flex
            as="button"
            type="button"
            aria-label={label}
            disabled={isDisabled}
            onClick={onClick}
            height="24px"
            minWidth="24px"
            paddingX={1}
            alignItems="center"
            justifyContent="center"
            borderRadius="sm"
            backgroundColor="transparent"
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            opacity={isDisabled ? 0.4 : 1}
            _hover={isDisabled ? undefined : { backgroundColor: 'interactive.hover' }}
            flexShrink={0}
        >
            <Text variant="caption" fontFamily="mono">
                {children}
            </Text>
        </Flex>
    );
}
